/**
 * 리포트 단위 «지도 결함 레이어» 동기화 — Facade (Strangler).
 *
 * - recordSyncIntent: 트리거·Vuex와 맵 사이 의도 로그
 * - refreshReportDefectLayerFull: Facade + runWfs 한 번에 (맵 컴포넌트 기본 진입)
 * - runReportDefectLayerSync: Facade 껍데기만 (execute 주입)
 * - runWfsDefectLayerRefresh: ensure → build → apply (wfsDefectLayerSync.js)
 *
 * 패턴: Facade + PipelinePhase + SyncReason + correlationId
 */
import {
  logPipeline,
  isPipelineLogEnabled,
  nextPipelineCorrelationId
} from './logger';
import { PipelinePhase } from './phases';
import { SyncReason } from './syncReasons';
import { runWfsDefectLayerRefresh } from './wfsDefectLayerSync';

export { PipelinePhase, SyncReason, logPipeline, isPipelineLogEnabled, nextPipelineCorrelationId };

export {
  ensureDefectTypesForWfs,
  buildDefectCodesForWfs,
  applyWfsAndAiDefectLayers,
  runWfsDefectLayerRefresh
} from './wfsDefectLayerSync';

/**
 * Strangler Facade: WFS·AI 결함 레이어 갱신 실행은 최종적으로 이 함수만 거치도록 옮길 예정.
 * 현재는 로그 후 `execute()`에 기존 DotSolarMap 호출을 위임한다.
 *
 * @param {{ correlationId?: number, reportId: string|number|null, layerSeq?: number, source?: string, execute: () => Promise<void>|void }} p
 */
export async function runReportDefectLayerSync({
  correlationId,
  reportId,
  layerSeq,
  source,
  execute
}) {
  logPipeline('phase', 'facade runReportDefectLayerSync → execute()', {
    phase: PipelinePhase.FETCH_WFS,
    correlationId,
    reportId,
    layerSeq,
    source
  });
  if (typeof execute !== 'function') {
    logPipeline('warn', 'runReportDefectLayerSync: missing execute', { correlationId, reportId });
    return undefined;
  }
  return execute();
}

/**
 * Facade(`runReportDefectLayerSync`) + `runWfsDefectLayerRefresh` 를 한 호출로 묶음.
 * DotSolarMapBox 의 debounced 실행 본문은 이 함수만 호출하면 됨.
 *
 * @param {{ correlationId?: number, reportId: string|number|null, layerSeq?: number, source?: string, wfsContext: Record<string, unknown> }} p
 */
export async function refreshReportDefectLayerFull({
  correlationId,
  reportId,
  layerSeq,
  source,
  wfsContext
}) {
  return runReportDefectLayerSync({
    correlationId,
    reportId,
    layerSeq,
    source,
    execute: () => runWfsDefectLayerRefresh(wfsContext)
  });
}

/**
 * 지도 결함 레이어 갱신 «의도»만 기록 (부작용 없음).
 * @param {{ reportId: string|number|null, reason?: string, source?: string, extra?: Record<string, unknown> }} p
 */
export function recordSyncIntent({ reportId, reason = SyncReason.UNKNOWN, source = '', extra = null }) {
  logPipeline('intent', 'sync intent for report defect layer', {
    phase: PipelinePhase.INTENT,
    reportId,
    reason,
    source,
    at: new Date().toISOString(),
    ...(extra && typeof extra === 'object' ? { extra } : {})
  });
}
